import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, Trash2, Loader2, MessageSquare, Flag } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminComments() {
  const { authFetch } = useAdmin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all / visible / hidden / flagged

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/comments`);
      const d = await r.json();
      setRows(d.comments || d.items || (Array.isArray(d) ? d : []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const act = async (id, action) => {
    const method = action === 'delete' ? 'DELETE' : 'POST';
    const url = action === 'delete' ? `${API}/admin/comments/${id}` : `${API}/admin/comments/${id}/${action}`;
    const r = await authFetch(url, { method });
    if (r.ok) { toast.success('Spremljeno'); load(); } else toast.error('Greška');
  };

  const filtered = rows.filter(c => {
    if (filter === 'visible') return !c.hidden;
    if (filter === 'hidden') return c.hidden;
    if (filter === 'flagged') return (c.flags || 0) > 0;
    return true;
  });

  return (
    <div className="p-4 md:p-6" data-testid="admin-comments">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Komentari</h1>
          <p className="text-sm text-zinc-400">Moderacija komentara članaka.</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {['all', 'visible', 'hidden', 'flagged'].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                  className={`px-3 py-1.5 rounded text-xs font-semibold ${filter === f ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
            {f === 'all' ? 'Svi' : f === 'visible' ? 'Vidljivi' : f === 'hidden' ? 'Sakriveni' : 'Prijavljeni'}
            <span className="ml-1.5 text-[10px] opacity-60">({rows.filter(c => f === 'all' ? true : f === 'visible' ? !c.hidden : f === 'hidden' ? c.hidden : (c.flags || 0) > 0).length})</span>
          </button>
        ))}
      </div>
      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="space-y-3">
          {filtered.map(c => (
            <div key={c.id} data-testid={`comment-${c.id}`}
                 className={`p-4 rounded-lg border ${c.hidden ? 'border-zinc-700 bg-zinc-900/50 opacity-60' : 'border-zinc-800 bg-zinc-900'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-zinc-500 mb-1">
                    <span className="font-semibold text-zinc-300">{c.author_name || c.user_name || 'Anoniman'}</span>
                    <span className="mx-2">·</span>
                    <span>{(c.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {c.article_slug && <span className="mx-2">·</span>}
                    {c.article_slug && <a href={`/clanak/${c.article_slug}`} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">{c.article_slug}</a>}
                  </div>
                  <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words">{c.body || c.text || c.content || ''}</p>
                  {(c.flags || 0) > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-400"><Flag size={10} /> {c.flags} prijava</div>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {c.hidden ? (
                    <button onClick={() => act(c.id, 'unhide')} data-testid={`unhide-${c.id}`}
                            className="p-1.5 rounded bg-emerald-700/60 hover:bg-emerald-700 text-white"><Eye size={14}/></button>
                  ) : (
                    <button onClick={() => act(c.id, 'hide')} data-testid={`hide-${c.id}`}
                            className="p-1.5 rounded bg-amber-700/60 hover:bg-amber-700 text-white"><EyeOff size={14}/></button>
                  )}
                  <button onClick={() => window.confirm('Izbrisati komentar?') && act(c.id, 'delete')}
                          data-testid={`delete-${c.id}`}
                          className="p-1.5 rounded bg-red-700/60 hover:bg-red-700 text-white"><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-8 text-zinc-500">Nema komentara za prikaz.</div>}
        </div>
      )}
    </div>
  );
}
